import { useCallback, useEffect, useRef } from 'react'

const store = new Map<string, [number, number]>()

export const usePersistentScroll = (id: string, isMounted = true) => {
  const elementRef = useRef<HTMLElement | null>(null)

  const restoreScroll = useCallback(
    (target: { scrollTo(x: number, y: number): void }) => {
      const pos = store.get(id)

      if (pos) {
        target.scrollTo(...pos)
      } else {
        target.scrollTo(0, 0)
      }
    },
    [id],
  )

  const storeScroll = useCallback(
    (target: { scrollX?: number; scrollLeft?: number; scrollY?: number; scrollTop?: number }) => {
      store.set(id, [target.scrollX || target.scrollLeft || 0, target.scrollY || target.scrollTop || 0])
    },
    [id],
  )

  const scrollRef = useCallback(
    (element: HTMLElement | null) => {
      if (element) {
        restoreScroll(element)
        elementRef.current = element
      }
    },
    [restoreScroll],
  )

  useEffect(() => {
    if (!isMounted) return

    const target = elementRef.current || window
    const handler = () => storeScroll(target)
    restoreScroll(elementRef.current || window)
    target.addEventListener('scroll', handler)

    return () => {
      target.removeEventListener('scroll', handler)
    }
  }, [isMounted, restoreScroll, storeScroll])

  return { scrollRef }
}
